// © Microsoft Corporation. All rights reserved.

import { AppError, SocialDataConnectorNotFound } from "@msnews/diagnostics";
import { IRequestContext, InspectorResponse, ScreenWidth, appEnvironment } from "@msnews/core";
import { SocialDataActions, SocialDataConnector } from "@msnews/social-data-connector";

import { ComponentConnector } from "@msnews/experiences-connector";
import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { DynamicReducer } from "@msnews/experiences-dynamic-reducer";
import { ExperienceInitialProps } from "@msnews/experiences-base";
import { IReducer } from "@msnews/experiences-redux-contracts";
import { PageBase } from "@msnews/experiences-page-base";
import { SocialBarActions } from "./SocialBar.actions";
import { SocialBarConfig } from "./SocialBar.config";
import { SocialBarInspector } from "./SocialBar.inspector";
import { SocialBarProps } from "./index";
import { SocialBarState } from "./SocialBar.state";
import { Store } from "redux";
import { appErrorLogger } from "@msnews/social-utils";

// A partial config to ensure loading of social data connector.
// This hard-coded config can be removed after making sure that all config documents contain the data connector.
const socialDataConnectorConfig: Partial<SocialBarConfig> = {
    dataConnectors: [
        {
            screenWidth: ScreenWidth.Any,
            children: [
                {
                    experienceType: ConnectorNamespaces.SocialData,
                    instanceId: "",
                    href: "cms/api/amp/experienceConfigIndex/BB1cKaeg"
                }
            ]
        }
    ]
};

/**
 * The connector for the SocialBar Experience.
 */
export class SocialBarConnector extends ComponentConnector<SocialBarConfig, SocialBarState, SocialBarProps, any> {

    private socialDataConnector: SocialDataConnector;

    /** @inheritdoc */
    public constructor(namespace: string, parentNamespace: string, rootReducer: DynamicReducer, reducer: IReducer<SocialBarState>, store: Store<any>, config: SocialBarConfig, requestContext: IRequestContext) {
        super(namespace, parentNamespace, rootReducer, reducer, store, {...socialDataConnectorConfig, ...config}, requestContext);
    }

    /** @inheritdoc */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {
        if (!appErrorLogger.onAppError) {
            appErrorLogger.setOnAppError(this.onAppError.bind(this));
        }
        await ComponentConnector.prototype.onComponentConnect.call(this, connectorProps);

        const market = connectorProps && connectorProps.context && connectorProps.context.market ? connectorProps.context.market : appEnvironment.CurrentMarket;

        PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.SocialData).then((socialDataConnector: SocialDataConnector) => {
            if (socialDataConnector) {
                this.socialDataConnector = socialDataConnector;
                this.registerObservers();
                this.updateSocialBarState();

                this.setComponent(market, connectorProps.context.theme, connectorProps.context.id);
            } else {
                this.onAppError({
                    ...SocialDataConnectorNotFound,
                    message: "SocialBar data connector not found in the root reducer",
                    pb: {
                        ...SocialDataConnectorNotFound.pb,
                        customMessage: "SocialBar data connector not found in the root reducer"
                    }
                } as AppError);
            }
        });
    }

    public registerObservers(): void {
        SocialDataActions.setTheme.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.updateMe.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.setContent.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.updateContent.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.updateContentRating.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.updateUser.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.updateResponsiveClass.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.mapCmsIdToContentId.registerObserver(() => this.updateSocialBarState());
        SocialDataActions.resetError.registerObserver(() => this.updateSocialBarState());
    }

    public updateSocialBarState(): void {
        if (this.getSocialDataConnector()) {
            SocialBarActions.setSocialData.getActionSender(this).send(this.socialDataConnector.getCurrentState());
        }
    }

    /** @inheritdoc */
    public mapStateToProps(state: SocialBarState): SocialBarProps {
        const inspectorResult: InspectorResponse<SocialBarProps> = SocialBarInspector.inspect({
            config: this.config,
            children: this.childComponentInstanceList,
            state: state
        });

        let connectorFunctions = {};
        if (this.getSocialDataConnector()) {
            connectorFunctions = this.socialDataConnector.getSocialBarConnectorFunctionsWithStateBindings();
        }

        return {
            ...inspectorResult.props,
            ...this.telemetryTags,
            children: this.childComponentInstanceList,
            mainTelemetryObject: this.telemetryObject,
            ...connectorFunctions
        };
    }

    public async setComponent(market: string, theme: string, id?: string) {
        SocialBarActions.setComponent.getActionSender(this).send(id, market);
        this.socialDataConnector.setTheme(theme);
    }

    private getSocialDataConnector() {
        if (!this.socialDataConnector)  {
            this.onAppError({
                ...SocialDataConnectorNotFound,
                message: "Social data connector not found in the root reducer",
                pb: {
                    ...SocialDataConnectorNotFound.pb,
                    customMessage: "Social data connector not found in the root reducer"
                }
            } as AppError);
        }
        return this.socialDataConnector;
    }
}